import { forwardRef, useCallback } from "react";
import { Input } from "@/components/ui/input";

/**
 * 24-hour-only time input.
 *
 * The native `<input type="time">` follows the browser/OS locale, which
 * means en-US laptops show an AM/PM picker even when the page declares
 * `lang="en-NZ"`. We need a guaranteed 24-hour UI everywhere, so this
 * component renders a plain text input that:
 *   • Accepts only digits and `:`
 *   • Auto-inserts the `:` after two digits
 *   • Validates against `HH:MM` on blur and snaps to a valid value
 *   • Reports its value via `onChange(value)` exactly as `"HH:MM"`
 *
 * On mobile, `inputMode="numeric"` brings up the number pad.
 */
const TimeInput24 = forwardRef(function TimeInput24(
  { value, onChange, "data-testid": testId, ...rest },
  ref
) {
  const handleChange = useCallback(
    (e) => {
      // Allow digits and ONE colon, while typing. Cap at 5 chars (HH:MM).
      let raw = (e.target.value || "").replace(/[^\d:]/g, "");
      const firstColon = raw.indexOf(":");
      if (firstColon !== -1) {
        raw = raw.slice(0, firstColon + 1) + raw.slice(firstColon + 1).replace(/:/g, "");
      }
      // Don't auto-insert colon while typing — saves a lot of UX edge cases.
      // The blur handler normalises "230" -> "02:30", "9" -> "09:00", etc.
      onChange?.(raw.slice(0, 5));
    },
    [onChange]
  );

  const handleBlur = useCallback(() => {
    if (!value) return;
    let h = 0, mm = 0;
    if (value.includes(":")) {
      const [hPart, mPart] = value.split(":");
      h = parseInt(hPart || "0", 10);
      mm = parseInt(mPart || "0", 10);
    } else {
      // Bare digits: interpret based on length.
      //   "9"     -> 9:00         (hours only)
      //   "17"    -> 17:00        (hours only)
      //   "930"   -> 9:30         (H + MM)
      //   "1730"  -> 17:30        (HH + MM)
      const digits = value.replace(/\D/g, "");
      if (digits.length === 0) { onChange?.(""); return; }
      if (digits.length <= 2) {
        h = parseInt(digits, 10);
      } else if (digits.length === 3) {
        h = parseInt(digits.slice(0, 1), 10);
        mm = parseInt(digits.slice(1), 10);
      } else {
        const padded = digits.slice(-4);
        h = parseInt(padded.slice(0, 2), 10);
        mm = parseInt(padded.slice(2, 4), 10);
      }
    }
    if (Number.isNaN(h) || Number.isNaN(mm)) { onChange?.(""); return; }
    h = Math.min(23, Math.max(0, h));
    mm = Math.min(59, Math.max(0, mm));
    const out = `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
    if (out !== value) onChange?.(out);
  }, [value, onChange]);

  return (
    <Input
      ref={ref}
      type="text"
      inputMode="numeric"
      placeholder="HH:MM"
      maxLength={5}
      value={value || ""}
      onChange={handleChange}
      onBlur={handleBlur}
      data-testid={testId}
      {...rest}
    />
  );
});

export default TimeInput24;
